import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const NewMessageModal = ({ onClose, onConversationCreated, preselectedUser }) => {
  const { authFetch } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  // Handle preselected user from search
  useEffect(() => {
    if (preselectedUser) {
      // Validate that we can message this user
      const checkAndSelect = async () => {
        try {
          const res = await authFetch(`/api/messages/can-message/${preselectedUser.id}`);
          if (res.ok) {
            const data = await res.json();
            if (data.can_message) {
              setSelectedUser(preselectedUser);
            } else {
              setError(data.reason || 'Cannot message this user');
            }
          }
        } catch (err) {
          setError('Failed to check message permissions');
        }
      };
      checkAndSelect();
    }
  }, [preselectedUser, authFetch]);

  const handleSearch = useCallback(async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const res = await authFetch(`/api/users/search?q=${encodeURIComponent(query)}&limit=10`);
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.users || []);
      }
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearching(false);
    }
  }, [authFetch]);

  const handleSelectUser = async (user) => {
    setError('');
    // Check if we can message this user
    try {
      const res = await authFetch(`/api/messages/can-message/${user.id}`);
      if (res.ok) {
        const data = await res.json();
        if (data.can_message) {
          setSelectedUser(user);
          setSearchQuery('');
          setSearchResults([]);
        } else {
          setError(data.reason || 'Cannot message this user');
        }
      }
    } catch (err) {
      setError('Failed to check message permissions');
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!selectedUser || !message.trim() || sending) return;

    setSending(true);
    setError('');

    try {
      const res = await authFetch('/api/messages/send', {
        method: 'POST',
        body: JSON.stringify({
          recipient_id: selectedUser.id,
          content: message.trim()
        })
      });

      if (res.ok) {
        const data = await res.json();
        onConversationCreated(data.conversation_id);
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to send message');
      }
    } catch (err) {
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>New Message</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {error && <div className="modal-error">{error}</div>}

          {!selectedUser ? (
            <>
              <div className="form-group">
                <label>Search for a user</label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Enter name or email..."
                  autoFocus
                />
              </div>

              {searching && (
                <div className="search-loading">Searching...</div>
              )}

              {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
                <div className="search-empty">No users found</div>
              )}

              {searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.map((user) => {
                    let profileImageUrl = user.profile_image;
                    if (profileImageUrl && !profileImageUrl.startsWith('http')) {
                      profileImageUrl = `${API_BASE_URL}${profileImageUrl}`;
                    }

                    return (
                      <div
                        key={user.id}
                        className="search-result-item"
                        onClick={() => handleSelectUser(user)}
                      >
                        <div className={`search-result-avatar ${profileImageUrl ? 'has-image' : ''}`}>
                          {profileImageUrl ? (
                            <img src={profileImageUrl} alt={user.name || 'User'} />
                          ) : (
                            (user.name || user.email || '?').charAt(0).toUpperCase()
                          )}
                        </div>
                        <div className="search-result-info">
                          <span className="search-result-name">{user.name || 'No name'}</span>
                          <span className="search-result-email">{user.email}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <form onSubmit={handleSend}>
              <div className="selected-user">
                <span className="selected-user__label">To:</span>
                <span className="selected-user__name">
                  {selectedUser.name || selectedUser.email}
                </span>
                <button
                  type="button"
                  className="selected-user__remove"
                  onClick={() => setSelectedUser(null)}
                >
                  &times;
                </button>
              </div>

              <div className="form-group">
                <label>Message</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Write your message..."
                  rows={4}
                  maxLength={2000}
                  autoFocus
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={!message.trim() || sending}
                >
                  {sending ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewMessageModal;
